import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, type JSX } from "react";
import AppLayout from "../components/layout/AppLayout";
import Login from "../pages/LoginPage";
import ProtectedRoute from "./ProtectedRoutes";
import AccountInvestigate from "../pages/Exceptions/investigate/accountInvestigate/AccountInvestigate";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";

const ProductSetup = lazy(()=> import ("../pages/Settings/ProductSetup/productSetup"))
const ResponseCodeSetup = lazy(()=> import ("../pages/Settings/ResponseCodeSetup/responseCodeSetup"))
const JournalEntryReport = lazy(()=> import ( "../pages/Recon/Reports/JournalReport/JournalReports"));
const ReportsLayout = lazy(() => import ("../pages/Recon/Reports/General/ReportsLayout"));
const Dashboard = lazy(() => import("../modules/dashboard/Dashboard"));
const Investigate = lazy(() => import("../pages/Exceptions/investigate/channelInvestigate/Investigate"));
const Matchingrule = lazy(() => import( "../pages/Settings/MatchingRule/Matchingrule"));
const AuditLog = lazy(() => import( "../pages/Audit/Audit"));
const ReconRunsList = lazy(() => import( "../pages/Audit/ReconRunsList"));
const Aimatch = lazy(() => import( "../pages/Aimatch"));
const ChannelUpload = lazy(() => import( "../pages/Upload/Upload"));
const DDACCC = lazy(() => import( "../pages/Recon/Accounts/DDACC"));
const GL = lazy(() => import( "../pages/Recon/Accounts/GL"));
const NOSTRO = lazy(() => import( "../pages//Recon/Accounts/NOSTRO"));
const RTGS = lazy(() => import( "../pages/Recon/Accounts/RTGS"));
const ATM = lazy(() => import( "../pages/Recon/Channel/Atm"));
const InternetBanking = lazy(() => import( "../pages/Recon/Channel/Internetbanking"));
const Wallet = lazy(() => import( "../pages/Recon/Channel/Wallet"));
const Ecommerce = lazy(() => import( "../pages/Recon/Channel/Ecommerce"));
const BillPayment = lazy(() => import( "../pages/Recon/Channel/Bill_payments"));
const Pos = lazy(() => import( "../pages/Recon/Channel/Pos"));
const Emoney = lazy(() => import( "../pages/Recon/Channel/Emoney"));
const MobileMoney = lazy(() => import( "../pages/Recon/Channel/MobileMoney"));
const EFT = lazy(() => import( "../pages/Recon/Channel/Eft"));
const MasterCard = lazy(() => import( "../pages/Recon/Channel/Card/MasterCard"));
const Visa = lazy(() => import( "../pages/Recon/Channel/Card/Visa"));
const NFS = lazy(() => import( "../pages/Recon/Channel/Card/Nfs"));
const NewInvestigate = lazy(() => import( "../pages/Exceptions/investigate/channelInvestigate/Investigate"));
const QuickMatchingRule = lazy(() => import( "../pages/Exceptions/investigate/quickMatchingRule/QuickMatchingRule"));
const UserManagement = lazy(() => import( "../pages/SystemAdministration/usermanagement"));

const TemplateShowcase = lazy(() => import( "../pages/Settings/TemplateShowcase"));
const RoleManagement = lazy(() => import( "../pages/SystemAdministration/RoleManagement"));
const ModulePermissionManagement = lazy(() => import( "../pages/SystemAdministration/module/ModuleConfig"));
const SystemConfiguration = lazy(() => import( "../pages/Settings/SystemConfiguration/SystemConfiguration"));
const GeneralLedger = lazy(() => import( "../pages/Settings/GeneralLedger/GeneralLedger"));

// Create a wrapper component for role-based routes
const RoleProtectedRoute = ({ 
  element, 
  requiredModules 
}: { 
  element: JSX.Element; 
  requiredModules: string[] 
}) => {
  const hasAccess = checkUserRole(requiredModules);
  
  if (!hasAccess) {
    // Redirect to dashboard or show unauthorized page
    return <Navigate to="/" replace />;
  }
  
  return element;
};

const parsePermissionJson = (permissionJson: string): any[] => {
  try {
    return JSON.parse(permissionJson);
  } catch (error) {
    console.error("Invalid permission_json", error);
    return [];
  }
};

const getAllModuleNamesFromRoles = (roles: any): string[] => {
  const moduleSet = new Set<string>();

  roles.forEach((role: any) => {
    const permissions = parsePermissionJson(role.permission_json);

    permissions.forEach((perm: any) => {
      if (perm.moduleName) {
        moduleSet.add(perm.moduleName.toLowerCase());
      }
    });
  });

  return Array.from(moduleSet);
};

const checkUserRole = (modules: string[]) => {
  let userRoleDetails = localStorage.getItem("user_role_details");
  if (!userRoleDetails) {
    console.error("user_role_details not found in localStorage");
    return false;
  }
  
  let userDetails = JSON.parse(userRoleDetails);
  const allowedModules = getAllModuleNamesFromRoles(userDetails.roles);
  
  if (!Array.isArray(modules)) return false;
  // console.log("Allowed Modules:", allowedModules);
  return modules.some((m) => allowedModules.includes(m.toLowerCase()));
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          {/* Dashboard - Accessible to all authenticated users */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/priority" element={<Dashboard cat="priority" />} />
          <Route path="/atm" element={<Dashboard cat="atm" />} />
          <Route path="/pos" element={<Dashboard cat="pos" />} />
          <Route path="/cards" element={<Dashboard cat="cards" />} />
          <Route path="/mobile-money" element={<Dashboard cat="mobile-money" />} />
          <Route path="/bill-payments" element={<Dashboard cat="bill-payments" />} />
          <Route path="/eft" element={<Dashboard cat="eft" />} />
          <Route path="/internet-banking" element={<Dashboard cat="internet-banking" />} />
          <Route path="/e-money" element={<Dashboard cat="e-money" />} />
          <Route path="/e-commerce" element={<Dashboard cat="e-commerce" />} />

          {/* Upload - Requires 'upload' permission */}
          <Route 
            path="/upload" 
            element={
              <RoleProtectedRoute 
                element={<ChannelUpload />} 
                requiredModules={['upload']} 
              />
            } 
          />

          {/* Recon Channel - Requires 'recon' or 'channel' permission */}
          <Route 
            path="/recon/channel/atm" 
            element={
              <RoleProtectedRoute 
                element={<ATM />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/pos" 
            element={
              <RoleProtectedRoute 
                element={<Pos />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/cards/nfs" 
            element={
              <RoleProtectedRoute 
                element={<NFS />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/cards/visa" 
            element={
              <RoleProtectedRoute 
                element={<Visa />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/cards/master-card" 
            element={
              <RoleProtectedRoute 
                element={<MasterCard />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/e-money" 
            element={
              <RoleProtectedRoute 
                element={<Emoney />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/mobile-money" 
            element={
              <RoleProtectedRoute 
                element={<MobileMoney />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/bill-payments" 
            element={
              <RoleProtectedRoute 
                element={<BillPayment />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/eft" 
            element={
              <RoleProtectedRoute 
                element={<EFT />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/internet-banking" 
            element={
              <RoleProtectedRoute 
                element={<InternetBanking />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/wallet" 
            element={
              <RoleProtectedRoute 
                element={<Wallet />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/recon/channel/ecommerce" 
            element={
              <RoleProtectedRoute 
                element={<Ecommerce />} 
                requiredModules={['channels']} 
              />
            } 
          />

          {/* Channel Account - Requires 'recon' or 'accounts' permission */}
          <Route 
            path="/recon/accounts/ddacc" 
            element={
              <RoleProtectedRoute 
                element={<DDACCC />} 
                requiredModules={['accounts']} 
              />
            } 
          />
          <Route 
            path="/recon/accounts/gl" 
            element={
              <RoleProtectedRoute 
                element={<GL />} 
                requiredModules={['accounts']} 
              />
            } 
          />
          <Route 
            path="/recon/accounts/nostro" 
            element={
              <RoleProtectedRoute 
                element={<NOSTRO />} 
                requiredModules={['accounts']} 
              />
            } 
          />
          <Route 
            path="/recon/accounts/rtgs" 
            element={
              <RoleProtectedRoute 
                element={<RTGS />} 
                requiredModules={['accounts']} 
              />
            } 
          />

          {/* Reports - Requires 'reports' permission */}
          <Route 
            path="/general-reports" 
            element={
              <RoleProtectedRoute 
                element={<ReportsLayout />} 
                requiredModules={['reports']} 
              />
            } 
          />
          <Route 
            path="/journal-reports" 
            element={
              <RoleProtectedRoute 
                element={<JournalEntryReport />} 
                requiredModules={['reports']} 
              />
            } 
          />

          {/* AI Match - Requires 'aimatch' permission */}
          <Route 
            path="/aimatch" 
            element={
              <RoleProtectedRoute 
                element={<Aimatch />} 
                requiredModules={['ai_match']} 
              />
            } 
          />

          {/* System Administration - Requires 'admin' or 'system administration' permission */}
          <Route 
            path="/user-management" 
            element={
              <RoleProtectedRoute 
                element={<UserManagement />} 
                requiredModules={['system_administration']} 
              />
            } 
          />
          <Route 
            path="/role-management" 
            element={
              <RoleProtectedRoute 
                element={<RoleManagement />} 
                requiredModules={['system_administration']} 
              />
            } 
          />
          <Route 
            path="/module-config" 
            element={
              <RoleProtectedRoute 
                element={<ModulePermissionManagement />} 
                requiredModules={['system_administration']} 
              />
            } 
          />

          {/* Settings - Requires 'settings' permission */}
          <Route 
            path="/matchingrule" 
            element={
              <RoleProtectedRoute 
                element={<Matchingrule />} 
                requiredModules={['settings']} 
              />
            } 
          />
          <Route 
            path="/general-ledger" 
            element={
              <RoleProtectedRoute 
                element={<GeneralLedger />} 
                requiredModules={['settings']} 
              />
            } 
          />
           <Route 
            path="/product-setup" 
            element={
              <RoleProtectedRoute 
                element={<ProductSetup />} 
                requiredModules={['settings']} 
              />
            } 
          />
          <Route 
            path="/response-code-setup" 
            element={
              <RoleProtectedRoute 
                element={<ResponseCodeSetup />} 
                requiredModules={['settings']} 
              />
            } 
          />
          <Route 
            path="/system-configuration" 
            element={
              <RoleProtectedRoute 
                element={<SystemConfiguration />} 
                requiredModules={['settings']} 
              />
            } 
          />

          {/* Audit Log - Requires 'auditlog' permission */}
          <Route 
            path="/auditlog" 
            element={
              <RoleProtectedRoute 
                element={<ReconRunsList />} 
                requiredModules={['audit-log']} 
              />
            } 
          />
          <Route 
            path="/auditlogDetails/:reconGroupNumber" 
            element={
              <RoleProtectedRoute 
                element={<AuditLog />} 
                requiredModules={['audit-log']} 
              />
            } 
          />

          {/* Templates - Requires 'templates' or 'settings' permission */}
          <Route 
            path="/templates" 
            element={
              <RoleProtectedRoute 
                element={<TemplateShowcase />} 
                requiredModules={['settings']} 
              />
            } 
          />

          {/* Investigate - Requires 'investigate' or 'exceptions' permission */}
          <Route 
            path="/channel-investigate" 
            element={
              <RoleProtectedRoute 
                element={<Investigate />} 
                requiredModules={['channels']} 
              />
            } 
          />
          <Route 
            path="/account-investigate" 
            element={
              <RoleProtectedRoute 
                element={<AccountInvestigate />} 
                requiredModules={['accounts']} 
              />
            } 
          />

          {/* Quick Matching Rule - Requires 'matching rule' permission */}
          <Route 
            path="/quick-matching-rule" 
            element={
              <RoleProtectedRoute 
                element={<QuickMatchingRule />} 
                requiredModules={['settings']} 
              />
            } 
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
      
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;